<template>
  <div @click="toggleOverlayPanel">
    <div :class="$style.itemsWrapper">
      <div :class="$style.itemContent">
        <div v-for="item in items" :key="item.id" :class="$style.item">
          <img v-if="item.sprite" :src="item.sprite.imageUrl" :alt="item.name" :class="$style.spriteImage" />
          <span v-else :class="$style.spriteName">{{ item.name }}</span>
          x{{ item.quantity }}
        </div>
      </div>
      <i :class="[$style.chevronIcon, isPanelVisible ? 'pi pi-chevron-up' : 'pi pi-chevron-down']"></i>
    </div>

    <Popover ref="overlayPanel">
      <ItemsBundlesList
        :reward-items="formattedRewardItems"
        :show-confirmation="true"
        @save="handleSave"
        @remove-item="handleRemoveItem"
      />
    </Popover>
  </div>
</template>

<script setup lang="ts">
import ItemsBundlesList from "@/modules/ItemsBundles/ItemsBundlesList.vue";
import { MaybeElementRef, onClickOutside } from "@vueuse/core";
import Popover from "primevue/popover";
import { computed, ref } from "vue";
import { DropdownItem, FormattedItem, Item } from "../types";

const props = defineProps<{
  items: Item[];
  dropdownItems: DropdownItem[];
  rewardId: string;
}>();

const emit = defineEmits<{
  (e: "update-items", payload: { rewardId: string; items: Item[] }): void;
  (e: "remove-item", payload: { rewardId: string; items: Item[] }): void;
}>();

const overlayPanel = ref<InstanceType<typeof Popover> | null>(null);
const isPanelVisible = ref(false);

const formattedRewardItems = computed<FormattedItem[]>(() =>
  props.items.map((item) => ({
    selectedItem: props.dropdownItems.find((dropdownItem) => dropdownItem.id === item.id) || null,
    quantity: item.quantity,
  }))
);

const toggleOverlayPanel = (event: Event) => {
  overlayPanel.value?.toggle(event);
  isPanelVisible.value = !isPanelVisible.value;
};

onClickOutside(overlayPanel as MaybeElementRef, () => {
  if (isPanelVisible.value) {
    isPanelVisible.value = false;
  }
});

const handleSave = (formattedItems: FormattedItem[]) => {
  const updatedItems: Item[] = formattedItems.map((item) => ({
    id: item.selectedItem?.id || "",
    name: item.selectedItem?.name || "",
    quantity: item.quantity,
    sprite: item.selectedItem?.sprite,
  }));
  emit("update-items", { rewardId: props.rewardId, items: updatedItems });
  overlayPanel.value?.hide();
  isPanelVisible.value = false;
};

const handleRemoveItem = (formattedItems: FormattedItem[]) => {
  const updatedItems: Item[] = formattedItems.map((item) => ({
    id: item.selectedItem?.id || "",
    name: item.selectedItem?.name || "",
    quantity: item.quantity,
    sprite: item.selectedItem?.sprite,
  }));
  emit("remove-item", { rewardId: props.rewardId, items: updatedItems });
  overlayPanel.value?.hide();
  isPanelVisible.value = false;
};
</script>

<style module>
.itemContent {
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  gap: 0.5rem;
}

.item {
  display: flex;
  align-items: center;
}

.spriteImage {
  width: 20px;
  height: 20px;
  margin-right: 0.5rem;
}

.spriteName {
  margin-right: 0.5rem;
}

.chevronIcon {
  margin-left: auto;
  cursor: pointer;
}

.itemsWrapper {
  display: flex;
  align-items: center;
}
</style>
